/**
 * The app's state: the replica (packages/core/src/replica.ts), persisted in IndexedDB, and one
 * sync loop. Every change shows at once (local first); sending happens in the background:
 * one request at a time, again after errors, when the network comes back, when the app returns to
 * the foreground, when the server announces something new, and every minute.
 */
import { FULL_ACCESS, PROTOCOL, PROTOCOL_HEADER, Replica, bookingToMl, canSee, changeToMl, factorsOf, seesPrices, uuidv7 } from "@lagerlog/core";
import type { Access as Rights, Booking, EntityChange, LoadResponse, Op, OpsResponse } from "@lagerlog/core";
import { addPending, readAll, saveConfig, saveSync, wipe } from "./idb.ts";
import type { Config, ReplicaMeta } from "./idb.ts";

export interface Status {
  online: boolean;
  syncing: boolean;
  lastSync: number | null;
  lastError: string | null;
  /** Duration of the last first load (ms), for the measurements. */
  lastLoadMs: number | null;
}

const TIMEOUT_MS = 10_000;
const RETRY = [1_500, 3_000, 6_000, 10_000];

export class ApiError extends Error {
  status: number;
  code: string | undefined;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** How a request shows its access: the spike's token, or the session cookie plus the workspace meant. */
export type Access = { token?: string; workspace?: string } | null;

const accessOf = (config: Config | null): Access => (config ? (config.token ? { token: config.token } : { workspace: config.workspaceId }) : null);

export async function api<T>(access: Access, path: string, init: RequestInit = {}, timeout = TIMEOUT_MS): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  const headers: Record<string, string> = { "content-type": "application/json", [PROTOCOL_HEADER]: String(PROTOCOL) };
  if (access?.token) headers.authorization = `Bearer ${access.token}`;
  else if (access?.workspace) headers["x-lagerlog-workspace"] = access.workspace;
  try {
    const res = await fetch(path, { ...init, signal: controller.signal, credentials: "same-origin", headers });
    if (!res.ok) {
      const text = await res.text();
      let message = text.slice(0, 200);
      let code: string | undefined;
      try {
        const body = JSON.parse(text) as { error?: string; code?: string };
        if (body.error) message = body.error;
        code = body.code;
      } catch {
        // not JSON: keep the text
      }
      throw new ApiError(res.status, message, code);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

function metaOf(r: Replica): ReplicaMeta {
  const { bookings: _b, pending: _p, ...meta } = r.toJSON();
  return meta;
}

class Store {
  config: Config | null = null;
  replica: Replica | null = null;
  status: Status = { online: navigator.onLine, syncing: false, lastSync: null, lastError: null, lastLoadMs: null };
  ready = false;
  /** Increases with every change; React re-renders on it. */
  version = 0;

  private readonly listeners = new Set<() => void>();
  private readonly pendingKeys = new Map<Op, number>();
  private running = false;
  private again = false;
  private retry = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private events: EventSource | null = null;

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getVersion = () => this.version;

  private emit() {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  async init() {
    const stored = await readAll();
    if (stored.config && stored.meta && (stored.config.protocol ?? 1) < PROTOCOL) {
      await this.upgrade(stored.config, Replica.fromJSON({ ...stored.meta, bookings: stored.bookings, pending: stored.pending.map((p) => p.op) }));
      this.ready = true;
      this.emit();
      return;
    }
    if (stored.config) {
      // Never loaded yet: nothing in bottles on this device, it starts in ml.
      this.config = { ...stored.config, protocol: PROTOCOL };
      if (stored.config.protocol !== PROTOCOL) await saveConfig(this.config);
      const replica = stored.meta
        ? Replica.fromJSON({ ...stored.meta, bookings: stored.bookings, pending: stored.pending.map((p) => p.op) })
        : new Replica(stored.config.lagers);
      stored.pending.forEach((p, i) => this.pendingKeys.set(replica.pending[i]!, p.key));
      this.replica = replica;
      this.start();
    }
    this.ready = true;
    this.emit();
  }

  /**
   * F18: the data on this device count wines in bottles. The waiting changes are converted to ml
   * against what the device knows of the articles (also those it created itself); everything else
   * is loaded anew from the server, which has converted its data.
   */
  private async upgrade(config: Config, old: Replica) {
    const view = old.entityView();
    const factorOf = factorsOf(view.values());
    const ops: Op[] = old.pending.map((op) =>
      op.type === "booking"
        ? { type: "booking", booking: bookingToMl(op.booking, factorOf) }
        : { type: "entity", change: changeToMl(op.change, old.entities.get(`${op.change.type}|${op.change.id}`)?.fields) },
    );
    await wipe();
    this.config = { ...config, protocol: PROTOCOL };
    await saveConfig(this.config);
    this.replica = new Replica(config.lagers);
    this.pendingKeys.clear();
    for (const op of ops) await this.push(op, false);
    this.start();
  }

  /**
   * Signed in again after the session ran out, into the workspace the device already holds: the
   * data and the waiting changes stay.
   */
  resume() {
    this.status = { ...this.status, lastError: null };
    this.retry = 0;
    this.events?.close();
    this.events = null;
    this.openEvents();
    this.emit();
    this.kick();
  }

  async updateConfig(patch: Partial<Config>) {
    if (!this.config) return;
    this.config = { ...this.config, ...patch };
    await saveConfig(this.config);
    this.emit();
  }

  /** The storages this device holds: changed, it loads them anew; waiting changes stay. */
  async setLagers(lagers: string[]) {
    if (!this.config || !this.replica) return;
    await this.updateConfig({ lagers });
    this.replica.lagers = [...lagers];
    this.replica.needsLoad = true;
    this.emit();
    this.kick();
  }

  /** Who is who in the workspace, for names next to bookings; asked at most once an hour. */
  private peopleAt = 0;
  async refreshPeople(force = false) {
    if (!this.config || this.config.token || !this.config.workspaceId) return;
    if (!force && Date.now() - this.peopleAt < 3_600_000) return;
    this.peopleAt = Date.now();
    try {
      const res = await api<{ members: { id: string; displayName: string }[] }>({ workspace: this.config.workspaceId }, "/api/workspace/members");
      await this.updateConfig({ people: Object.fromEntries(res.members.map((m) => [m.id, m.displayName])) });
    } catch {
      this.peopleAt = 0;
    }
  }

  /** What the signed-in member may (F20); with the fixed access of the spike everything. */
  rights(): Rights {
    return this.config?.access ?? FULL_ACCESS;
  }

  /** Amounts and values only for owners and managers; the server sends staff and viewers none. */
  get prices(): boolean {
    return seesPrices(this.rights().role);
  }

  /** Nothing granted at all: the app says so instead of showing empty lists. */
  get nothingGranted(): boolean {
    const lagers = this.rights().lagers;
    return lagers !== null && Object.keys(lagers).length === 0;
  }

  /**
   * The server says what the member may with every answer. When that changed (an owner gave or took a
   * storage, or another role), the device holds only storages it may see. Data loaded just now is
   * already as the member may see it; only storages taken on need loading. Data from before may hold
   * too much (a storage taken away, prices) or too little, so the device loads anew.
   */
  private async takeRights(access: Rights | undefined, replica: Replica, fresh: boolean): Promise<boolean> {
    if (!access || !this.config || JSON.stringify(access) === JSON.stringify(this.config.access)) return false;
    const known = this.config.access !== undefined;
    const asked = replica.lagers;
    let lagers = asked.filter((l) => canSee(access, l));
    // A device that holds nothing (any more) takes every storage it may now see.
    if (lagers.length === 0 && access.lagers) lagers = Object.keys(access.lagers);
    const added = lagers.some((l) => !asked.includes(l));
    await this.updateConfig({ access, lagers });
    replica.lagers = lagers;
    const reload = fresh ? added : known || added || lagers.length !== asked.length || !seesPrices(access.role);
    if (reload) replica.needsLoad = true;
    return reload;
  }

  /** The name of a person by account id, or null. */
  personName(id: string | undefined): string | null {
    const name = (id && this.config?.people?.[id]) || null;
    // Someone new (just invited): ask again, at most once a minute.
    if (id && !name && Date.now() - this.peopleAt > 60_000) void this.refreshPeople(true);
    return name;
  }

  async renameWorkspace(name: string) {
    if (!this.config) return;
    this.config = { ...this.config, workspace: name };
    await saveConfig(this.config);
    this.emit();
  }

  /** Signs the account out on the server too; the device keeps nothing. */
  async signOutAccount() {
    await api(null, "/api/logout", { method: "POST" }).catch(() => undefined);
    await this.signOut();
  }

  /** After entering the access: remember it and load the chosen storages. */
  async connect(config: Config) {
    await wipe();
    this.config = { ...config, protocol: PROTOCOL };
    await saveConfig(this.config);
    this.replica = new Replica(config.lagers);
    this.pendingKeys.clear();
    this.start();
    this.emit();
    await this.syncNow();
  }

  /** A request to the server for this workspace (reports, export); longer ones get more time. */
  request<T>(path: string, init: RequestInit = {}, timeout = TIMEOUT_MS): Promise<T> {
    return api<T>(accessOf(this.config), path, init, timeout);
  }

  /** Loads everything anew from the server, e.g. after a backup was imported. */
  async reload() {
    if (!this.config) return;
    this.stop();
    await this.connect(this.config);
  }

  async signOut() {
    this.stop();
    await wipe();
    this.config = null;
    this.replica = null;
    this.pendingKeys.clear();
    this.emit();
  }

  // ---- own changes: shown at once, stored, sent in the background ----

  async book(booking: Omit<Booking, "id" | "recordedAt"> & { at?: number }): Promise<Booking> {
    const now = Date.now();
    const person = this.config?.userId;
    const full = { ...booking, id: uuidv7(now), at: booking.at ?? now, recordedAt: now, device: this.config?.device, ...(person ? { person } : {}) } as Booking;
    await this.push({ type: "booking", booking: full });
    return full;
  }

  async edit(type: EntityChange["type"], id: string, fields: Record<string, unknown>) {
    const now = Date.now();
    await this.push({ type: "entity", change: { op: uuidv7(now), type, id, at: now, fields } });
  }

  private async push(op: Op, send = true) {
    const replica = this.replica!;
    if (op.type === "booking") replica.book(op.booking);
    else replica.edit(op.change);
    this.emit();
    this.pendingKeys.set(op, await addPending(op));
    if (send) this.kick();
  }

  // ---- sync loop ----

  private start() {
    window.addEventListener("online", this.onOnline);
    window.addEventListener("offline", this.onOffline);
    document.addEventListener("visibilitychange", this.onVisible);
    this.openEvents();
    this.schedule(60_000);
    this.kick();
  }

  private stop() {
    window.removeEventListener("online", this.onOnline);
    window.removeEventListener("offline", this.onOffline);
    document.removeEventListener("visibilitychange", this.onVisible);
    this.events?.close();
    this.events = null;
    if (this.timer) clearTimeout(this.timer);
  }

  private onOnline = () => {
    this.status = { ...this.status, online: true };
    this.retry = 0;
    this.openEvents();
    this.emit();
    this.kick();
  };

  private onOffline = () => {
    this.status = { ...this.status, online: false };
    this.emit();
  };

  private onVisible = () => {
    if (document.visibilityState === "visible") this.kick();
  };

  private openEvents() {
    if (!this.config || this.events || typeof EventSource === "undefined") return;
    const query = this.config.token ? `token=${encodeURIComponent(this.config.token)}` : `workspace=${encodeURIComponent(this.config.workspaceId ?? "")}`;
    const es = new EventSource(`/api/events?${query}`);
    es.addEventListener("seq", (e) => {
      if (Number((e as MessageEvent).data) > (this.replica?.seq ?? 0)) this.kick();
    });
    es.onerror = () => {
      // The browser reconnects by itself; nothing is lost, the loop also asks every minute.
    };
    this.events = es;
  }

  /**
   * Closes the event stream for printing. Safari counts the open stream as a page still loading
   * and holds a print back until loading ends, that is forever: nothing would appear on the
   * iPhone. The stream opens again after printing; the loop asks every minute meanwhile anyway.
   */
  pauseEventsForPrint() {
    if (!this.events) return;
    this.events.close();
    this.events = null;
    const again = () => {
      clearTimeout(timer);
      this.openEvents();
    };
    const timer = setTimeout(again, 120_000);
    // After the dialog, not right away: Safari may still be laying out the page.
    window.addEventListener("afterprint", () => setTimeout(again, 1000), { once: true });
  }

  private schedule(ms: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.kick();
      this.schedule(60_000);
    }, ms);
  }

  /** Starts a round of the loop, or asks for another one after the current. */
  kick() {
    if (this.running) {
      this.again = true;
      return;
    }
    void this.loop();
  }

  syncNow(): Promise<void> {
    return this.loop();
  }

  private async loop() {
    if (this.running || !this.config || !this.replica) return;
    this.running = true;
    this.status = { ...this.status, syncing: true };
    this.emit();
    try {
      do {
        this.again = false;
        await this.round();
      } while (this.again || (this.replica.pending.length > 0 && this.retry === 0) || this.replica.needsLoad);
      this.retry = 0;
      this.status = { ...this.status, online: true, lastSync: Date.now(), lastError: null };
      void this.refreshPeople();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const offline = !(err instanceof ApiError);
      this.status = { ...this.status, online: offline ? false : this.status.online, lastError: offline ? null : msg };
      const wait = RETRY[Math.min(this.retry, RETRY.length - 1)]!;
      this.retry++;
      if (err instanceof ApiError && err.status === 401) this.status = { ...this.status, lastError: this.config.token ? "Zugang ungültig" : "Bitte neu anmelden" };
      else this.schedule(wait);
    } finally {
      this.running = false;
      this.status = { ...this.status, syncing: false };
      this.emit();
    }
  }

  private async round() {
    const replica = this.replica!;
    const token = accessOf(this.config);
    if (replica.needsLoad) {
      const started = performance.now();
      const res = await api<LoadResponse>(token, `/api/load?lager=${encodeURIComponent(replica.lagers.join(","))}`);
      replica.applyLoad(res);
      // What the member may can have changed in between; then this load is already outdated.
      if (await this.takeRights(res.access, replica, true)) return;
      replica.state();
      this.status = { ...this.status, lastLoadMs: Math.round(performance.now() - started) };
      const confirmed = this.forgetConfirmed();
      await saveSync(metaOf(replica), res.bookings, true, confirmed);
      this.emit();
    }
    const req = replica.request();
    const res = await api<OpsResponse>(token, "/api/ops", { method: "POST", body: JSON.stringify(req) });
    replica.applySync(req.ops, res);
    await this.takeRights(res.access, replica, false);
    const confirmed = this.forgetConfirmed();
    await saveSync(metaOf(replica), res.bookings, false, confirmed);
    this.emit();
  }

  /** Keys of stored changes that are no longer pending (confirmed or refused). */
  private forgetConfirmed(): number[] {
    const still = new Set(this.replica!.pending);
    const keys: number[] = [];
    for (const [op, key] of this.pendingKeys) {
      if (still.has(op)) continue;
      keys.push(key);
      this.pendingKeys.delete(op);
    }
    return keys;
  }
}

export const store = new Store();
